import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import BottomIllustration from './assets/overview-bottom.svg';
import TopIllustration from './assets/overview-top.svg';

import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const Section = styled.section`
  ${fluidProp(['padding-bottom', 'padding-top'], '100px', '200px')}
  font-family: var(--typography-fontBrand);
  position: relative;
  overflow: hidden;

  ${ContentContainer} {
    position: relative;
    z-index: 1;
  }
`;

export const OverviewSectionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  ${fluidProp('margin-bottom', '100px', '180px')}

  &:nth-child(even) {
    flex-direction: row-reverse;

    ${tabletBreakpoint(css`
      flex-direction: column;
    `)}
  }

  ${tabletBreakpoint(css`
    flex-direction: column;
    align-items: flex-start;
  `)}
`;

export const OverviewSectionContent = styled.div`
  width: 45%;

  ${tabletBreakpoint(css`
    width: 90%;
  `)}
`;

export const OverviewTitleComponent = styled.div`
  ${({ theme }) => theme.typography.titleXL}
  font-family: var(--typography-fontBossa);
  margin: 26px 0 18px;

  > * {
    margin: inherit;
  }
`;

export const OverviewDescriptionComponent = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  color: var(--colors-bayoux);
`;

export const OverviewSectionIllustration = styled.div`
  width: 45%;
  background-image: var(--colors-zircon);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  max-height: 415px;
  max-width: 467px;

  ${tabletBreakpoint(css`
    margin-top: 30px;
    width: 90%;
  `)}
`;

export const TopIllustrationWrapper = styled.div`
  position: absolute;
  top: -5%;
  left: 0;
  display: flex;
  z-index: 0;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

export const BottomIllustrationWrapper = styled(TopIllustrationWrapper)`
  top: unset;
  left: unset;
  right: -5%;
  bottom: -3%;
`;

export function OverviewSlice({ slice }) {
  const { fields } = slice;

  return (
    <Section>
      <ContentContainer>
        {fields.map(({ icon, title, description, illustration }, index) => (
          <OverviewSectionRow key={index}>
            <OverviewSectionContent>
              {icon && (
                <Image
                  src={icon?.url}
                  alt={icon?.alt || ''}
                  width={icon?.dimensions.width || 54}
                  height={icon?.dimensions.height || 54}
                  quality="100"
                />
              )}
              {fieldHasContent(title) && (
                <OverviewTitleComponent>
                  <PrismicRichText field={title} />
                </OverviewTitleComponent>
              )}
              {fieldHasContent(description) && (
                <OverviewDescriptionComponent>
                  <PrismicRichText field={description} />
                </OverviewDescriptionComponent>
              )}
            </OverviewSectionContent>
            <OverviewSectionIllustration>
              <Image
                src={illustration?.url}
                alt={illustration?.alt || ''}
                width={illustration?.dimensions.width}
                height={illustration?.dimensions.height}
                quality="100"
              />
            </OverviewSectionIllustration>
          </OverviewSectionRow>
        ))}
      </ContentContainer>
      <TopIllustrationWrapper>
        <TopIllustration />
      </TopIllustrationWrapper>
      <BottomIllustrationWrapper>
        <BottomIllustration />
      </BottomIllustrationWrapper>
    </Section>
  );
}
